'use client'
import { useState, useEffect } from 'react'
import Link from 'next/link'

export default function DealsHero({ deals, totalDeals, dealsRef, currentUser, formatDealPrice }) {
  const heroDeals = [...deals]
    .sort((a, b) => (b.score ?? b.discountPct / 10) - (a.score ?? a.discountPct / 10))
    .slice(0, 4)
  const [heroIdx, setHeroIdx] = useState(0)
  useEffect(() => {
    if (heroDeals.length <= 1) return
    const t = setInterval(() => setHeroIdx((i) => (i + 1) % heroDeals.length), 3500)
    return () => clearInterval(t)
  }, [heroDeals.length])

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-blue-700 via-blue-600 to-indigo-700 px-4 pt-20 pb-28">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-20 h-[500px] w-[500px] rounded-full bg-blue-600/10 blur-3xl" />
        <div className="absolute -bottom-20 -left-20 h-[350px] w-[350px] rounded-full bg-violet-600/10 blur-3xl" />
      </div>
      <div className="relative mx-auto max-w-2xl text-center">
        <div className="mb-8 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 backdrop-blur-sm">
          <span className="h-2 w-2 animate-pulse rounded-full bg-green-400" />
          <span className="text-sm font-medium text-white/80">
            <span className="font-semibold text-white">{totalDeals ?? deals.length}</span> deals tracked this week
          </span>
        </div>
        <h1
          className="text-4xl font-bold tracking-tight text-white leading-tight sm:text-5xl"
          style={{ animation: 'fairHeroFade 0.7s ease-out both' }}
        >
          Cheap flights.<br className="hidden sm:block" /> No middleman.
        </h1>
        <p
          className="mt-6 text-lg text-slate-300 leading-relaxed"
          style={{ animation: 'fairHeroFade 0.7s 0.12s ease-out both' }}
        >
          We track 1,800+ routes and surface genuine price drops — you always book direct, no markup.
        </p>
        {heroDeals.length > 0 && (
          <div className="relative mt-8" style={{ height: '6rem' }}>
            {heroDeals.map((deal, i) => (
              <div
                key={deal.id}
                className={`absolute inset-0 transition-all duration-500${i !== heroIdx ? ' pointer-events-none' : ''}`}
                style={{
                  opacity: i === heroIdx ? 1 : 0,
                  transform: i === heroIdx ? 'translateY(0) scale(1)' : 'translateY(10px) scale(0.97)',
                }}
                aria-hidden={i !== heroIdx}
              >
                <Link href={`/deals/${deal.id}`} className="flex h-full items-center justify-between gap-3 rounded-2xl border border-white/20 bg-white/10 px-5 py-4 shadow-lg shadow-black/20 backdrop-blur-md" onClick={() => window.plausible?.('HeroCarouselCTAClick', { props: { route: `${deal.origin}-${deal.destination}` } })}>
                  <div className="min-w-0 text-left">
                    <p className="text-lg font-bold leading-tight text-white">{deal.origin} → {deal.destination}</p>
                    <p className="mt-0.5 truncate text-sm text-slate-400">{deal.airline ?? 'Multiple airlines'}</p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-2xl font-extrabold text-white">{formatDealPrice(deal.price).primary}</p>
                    {deal.discountPct && (
                      <span className="mt-0.5 inline-block rounded-full bg-green-500 px-2 py-0.5 text-xs font-bold text-white">
                        -{deal.discountPct}%
                      </span>
                    )}
                  </div>
                </Link>
              </div>
            ))}
          </div>
        )}
        {heroDeals.length > 1 && (
          <div className="mt-4 flex justify-center gap-1.5">
            {heroDeals.map((_, i) => (
              <button
                key={i}
                onClick={() => setHeroIdx(i)}
                className={`h-1.5 rounded-full transition-all ${i === heroIdx ? 'w-5 bg-white' : 'w-1.5 bg-white/30 hover:bg-white/50'}`}
                aria-label={`Show deal ${i + 1}`}
              />
            ))}
          </div>
        )}
        <div
          className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row"
          style={{ animation: 'fairHeroFade 0.7s 0.22s ease-out both' }}
        >
          <button
            onClick={() => dealsRef.current?.scrollIntoView({ behavior: 'smooth' })}
            className="w-full sm:w-auto rounded-xl bg-blue-500 px-8 py-3.5 text-base font-bold text-white shadow-lg shadow-blue-500/30 transition-colors hover:bg-blue-400 active:bg-blue-600"
          >
            Browse all {totalDeals ?? deals.length} deals →
          </button>
          {!currentUser && (
            <Link
              href="/signup"
              className="w-full sm:w-auto rounded-xl border border-white/20 bg-white/10 px-8 py-3.5 text-base font-semibold text-white transition-colors hover:bg-white/20 text-center"
            >
              Get deal alerts free
            </Link>
          )}
        </div>
      </div>
    </section>
  )
}
